import React, { useMemo, useCallback, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import MenuItem from "@mui/material/MenuItem"; // For dropdown
import { Alert } from "@mui/material"; // Import Alert for displaying error messages
import { toasty } from "../../Helper";
import { Input, Select, Spin } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { FormControl } from "@mui/material";
import cancelIcon from "../../app/assets/svgComponent/cancel.svg";
import saveIcon from "../../app/assets/svgComponent/save.svg";

const TableForm = ({
	item,
	organizations,
	departments,
	subDepartments,
	onChange,
	onSave,
	onClear,
	isEdit,
	isLoadingDepartments,
	isLoadingSubDepartments
}) => {
	const [error, setError] = useState("");
	
	// Add state to track original values
	const [originalValues, setOriginalValues] = useState({});

	// Set original values when component mounts or when item changes
	React.useEffect(() => {
		if (isEdit) {
			setOriginalValues({ ...item });
		}
	}, [isEdit, item]);

	// Control when fields should be enabled
	const isDepartmentDisabled = !item?.organization_id || isLoadingDepartments;
	const isSubDepartmentDisabled = !item?.department_id || isLoadingSubDepartments;
	const areOtherFieldsDisabled = !item?.subdepartment_id;

	// Simplified isFormValid function
	const isFormValid = () => {
		if (isEdit) {
			// Always allow saving in edit mode
			return true;
		}
		
		// For new entries, keep the strict validation
		return (
			item.organization_id &&
			item.department_id &&
			item.subdepartment_id &&
			item.role_name &&
			item.salary_min &&
			item.salary_max &&
			item.level &&
			item.designation &&
			item.employment_type &&
			item.work_arrangement &&
			item.location_id
		);
	};

	// Add validation function
	const validateForm = () => {
		const requiredFields = {
			organization_id: "Organization",
			department_id: "Department",
			subdepartment_id: "SubDepartment",
			role_name: "Role Name",
			salary_min: "Minimum Salary",
			salary_max: "Maximum Salary",
			level: "Level",
			designation: "Designation",
			employment_type: "Employment Type",
			work_arrangement: "Work Arrangement",
			location_id: "City",
			total_rounds: "Rounds"
		};

		for (const [field, label] of Object.entries(requiredFields)) {
			if (!item[field]) {
				setError(`${label} is required`);
				return false;
			}
		}

		// Additional validation for salary
		if (parseInt(item.salary_min) > parseInt(item.salary_max)) {
			setError("Minimum salary cannot be greater than maximum salary");
			return false;
		}

		setError("");
		return true;
	};

	// Modify onSave handler
	const handleSave = () => {
		if (!validateForm()) {
			return;
		}
		onSave();
	};

	const inputStyle = {
		width: "368px",
		height: "34px",
		padding: "6px 10px",
		gap: "8px",
		borderRadius: "6px",
		border: "1px solid rgba(255, 255, 255, 1)",
		boxShadow: "0px 1px 2px 0px rgba(17, 24, 39, 0.08), 0px 0px 0px 1px rgba(17, 24, 39, 0.1)",
		backgroundColor: "#F3F4F6",
		fontSize: "14px",
		fontFamily: "Inter"
	};

	const labelStyle = {
		display: "block",
		fontSize: "14px",
		color: "#374151",
		marginBottom: "8px",
		fontWeight: "500"
	};

	return (
		<div style={{ 
			width: "410px",
			height: "88vh",
			fontFamily: "inter",
			padding: "0",
			borderRadius: "12px",
			backgroundColor: "#FFFFFF",
			position: "fixed",
			right: "12px",
			top: "12px",
			boxShadow: `
				0px 20px 44px 0px rgba(0, 0, 0, 0.1),
				0px 80px 80px 0px rgba(0, 0, 0, 0.09),
				0px 180px 108px 0px rgba(0, 0, 0, 0.05),
				0px 319px 128px 0px rgba(0, 0, 0, 0.01),
				0px 499px 140px 0px rgba(0, 0, 0, 0)
			`,
			boxSizing: "border-box",
			display: "flex",
			flexDirection: "column",
			overflow: "hidden"
		}}>
			<div style={{
				width: "100%",
				height: "48px",
				padding: "8px 16px",
				borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				background: "rgba(247, 247, 248, 1)",
				flexShrink: 0
			}}>
				<div style={{ 
					display: "flex", 
					alignItems: "center", 
					gap: "6px"
				}}>
					<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M7.00156 4.19961H2.10156V3.14961C2.10156 2.57001 2.57196 2.09961 3.15156 2.09961H5.22706C5.46086 2.09961 5.67996 2.21651 5.80946 2.41111L7.00156 4.19961Z" fill="#2563EB" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
						<path d="M2.10156 4.19971H10.8516C11.4312 4.19971 11.9016 4.67011 11.9016 5.24971V9.79971C11.9016 10.9589 10.9608 11.8997 9.80156 11.8997H4.20156C3.04236 11.8997 2.10156 10.9589 2.10156 9.79971V4.19971Z" stroke="#2563EB" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
					<span style={{ 
						fontWeight: "500",
						fontSize: "16px"
					}}>
						{isEdit ? 'Edit role' : 'Create new role'}
					</span>
				</div>
			</div>

			<div style={{ 
				padding: "24px 12px 24px 24px", 
				width: "100%",
				flex: 1,
				overflowY: "auto",
				overflowX: "hidden"
			}}>
				{error && <Alert severity="error">{error}</Alert>}

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>Organization</label>
					<FormControl fullWidth>
						<Select
							value={item?.organization_name || ""}
							onChange={(value) => onChange({ target: { name: "organization_name", value } })}
							disabled={isEdit}
							style={{ ...inputStyle }}
							placeholder="choose from this list"
							loading={organizations.length === 0}
						>
							<Select.Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Select.Option>
							{organizations.map((org) => (
								<Select.Option key={org.id} value={org.name}>
									{org.name}
								</Select.Option>
							))}
						</Select>
					</FormControl>
				</div>

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>Department <span style={{ color: 'red' }}>*</span></label>
					<FormControl fullWidth>
						<Select
							value={item?.department_name || ""}
							onChange={(value) => onChange({ target: { name: "department_name", value } })}
							disabled={isDepartmentDisabled || isEdit}
							style={{ ...inputStyle }}
							placeholder="choose from this list"
							loading={isLoadingDepartments}
						>
							<Select.Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Select.Option>
							{departments.map((dept) => (
								<Select.Option key={dept.id} value={dept.name}>
									{dept.name}
								</Select.Option>
							))}
						</Select>
					</FormControl>
				</div>

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>Sub-Department <span style={{ color: 'red' }}>*</span></label>
					<FormControl fullWidth>
						<Select
							value={item?.subdepartment_name || ""}
							onChange={(value) => onChange({ target: { name: "subdepartment_name", value } })}
							disabled={isSubDepartmentDisabled || isEdit}
							style={{ ...inputStyle }}
							placeholder="choose from this list"
							loading={isLoadingSubDepartments}
						>
							<Select.Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Select.Option>
							{subDepartments.map((subDept) => (
								<Select.Option key={subDept.id} value={subDept.name}>
									{subDept.name}
								</Select.Option>
							))}
						</Select>
					</FormControl>
				</div>

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>Role Name <span style={{ color: 'red' }}>*</span></label>
					<FormControl fullWidth>
						<Input
							value={item.role_name || ""}
							onChange={(e) => onChange(e)}
							name="role_name"
							style={{...inputStyle}}
							disabled={areOtherFieldsDisabled}
							placeholder="Enter role name"
						/>
					</FormControl>
				</div>

				<div style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "16px" }}>
					<div style={{ flex: 1 }}>
						<label style={labelStyle}>Salary Min <span style={{ color: 'red' }}>*</span></label>
						<FormControl fullWidth>
							<Input
								value={item.salary_min || ""}
								onChange={(e) => onChange(e)}
								name="salary_min"
								type="text"
								style={{...inputStyle, width: "80%"}}
								disabled={areOtherFieldsDisabled}
								onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ''))}
								placeholder="x,xx,xxx"
							/>
						</FormControl>
					</div>
					<div style={{ flex: 1 }}>
						<label style={labelStyle}>Salary Max <span style={{ color: 'red' }}>*</span></label>
						<FormControl fullWidth>
							<Input
								value={item.salary_max || ""}
								onChange={(e) => onChange(e)}
								name="salary_max"
								type="text"
								style={{...inputStyle, width: "80%"}}
								disabled={areOtherFieldsDisabled}
								onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ''))}
								placeholder="xx,xx,xxx"
							/>
						</FormControl>
					</div>
				</div>

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>Level <span style={{ color: 'red' }}>*</span></label>
					<FormControl fullWidth>
						<Input
							value={item.level || ""}
							onChange={(e) => onChange(e)}
							name="level"
							type="text"
							style={{...inputStyle}}
							disabled={areOtherFieldsDisabled}
							onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ''))}
							placeholder="Enter level"
						/>
					</FormControl>
				</div>

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>Designation <span style={{ color: 'red' }}>*</span></label>
					<FormControl fullWidth>
						<Input
							value={item.designation || ""}
							onChange={(e) => onChange(e)}
							name="designation"
							style={{...inputStyle}}
							disabled={areOtherFieldsDisabled}
							placeholder="Enter designation"
						/>
					</FormControl>
				</div>

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>Employment Type <span style={{ color: 'red' }}>*</span></label>
					<FormControl fullWidth>
						<Select
							value={item.employment_type || ""}
							onChange={(value) => onChange({ target: { name: "employment_type", value } })}
							style={{ ...inputStyle }}
							placeholder="choose from this list"
						>
							<Select.Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Select.Option>
							<Select.Option value="FT">Full Time</Select.Option>
							<Select.Option value="PT">Part Time</Select.Option>
							 
						</Select>
					</FormControl>
				</div>

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>Work Arrangement <span style={{ color: 'red' }}>*</span></label>
					<FormControl fullWidth>
						<Select
							value={item.work_arrangement || ""}
							onChange={(value) => onChange({ target: { name: "work_arrangement", value } })}
							style={{ ...inputStyle }}
							placeholder="choose from this list"

						>
							<Select.Option value="" disabled>
								<span style={{ color: '#A9A9A9', fontSize: '14px' }}>choose from this list</span>
							</Select.Option>
							<Select.Option value="WFO">Work From Office</Select.Option>
							<Select.Option value="WFH">Work From Home</Select.Option>
							<Select.Option value="Hybrid">Hybrid</Select.Option>
						</Select>
					</FormControl>
				</div>

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>Rounds <span style={{ color: 'red' }}>*</span></label>
					<FormControl fullWidth>
						<Input
							value={item.total_rounds || ""}
							onChange={(e) => onChange(e)}
							name="total_rounds"
							type="text"
							style={{...inputStyle}}
							disabled={areOtherFieldsDisabled}
							onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ''))}
							placeholder="Enter number of rounds"
						/>
					</FormControl>
				</div>

				<div style={{ marginBottom: "24px" }}>
					<label style={labelStyle}>City <span style={{ color: 'red' }}>*</span></label>
					<FormControl fullWidth>
						<Input
							value={item.location_id || ""}
							onChange={(e) => onChange(e)}
							name="location_id"
							style={{...inputStyle}}
							disabled={areOtherFieldsDisabled}
							placeholder="Enter city"
						/>
					</FormControl>
				</div>
			</div>

			<div style={{ 
				width: "100%", 
				height: "80px", 
				display: "flex",
				gap: "10px",
				padding: "16px",
				background: "rgba(247, 247, 248, 1)",
				borderTop: "1px solid rgba(0, 0, 0, 0.1)",
				flexShrink: 0,
				justifyContent: "center",
				alignItems: "center",
				borderRadius: "12px 12px 0 0"
			}}>
				<img 
					src={cancelIcon} 
					onClick={onClear}
					alt="cancel" 
					style={{ 
						width: "180px",
						height: "50px",
						objectFit: "contain",
						cursor: "pointer",
						transition: "transform 0.2s"
					}}
					onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.95)"}
					onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
					onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
				/>
				
				<img 
					src={saveIcon} 
					onClick={handleSave}
					disabled={!isFormValid()}
					alt="save" 
					style={{ 
						width: "180px",
						height: "48px",
						objectFit: "contain",
						cursor: isFormValid() ? "pointer" : "not-allowed",
						transition: "transform 0.2s",
						opacity: isFormValid() ? 1 : 0.5
					}}
					onMouseDown={(e) => isFormValid() && (e.currentTarget.style.transform = "scale(0.95)")}
					onMouseUp={(e) => isFormValid() && (e.currentTarget.style.transform = "scale(1)")}
					onMouseLeave={(e) => isFormValid() && (e.currentTarget.style.transform = "scale(1)")}
				/>
			</div>
		</div>
	);
};

export default React.memo(TableForm);
